import { Component, Input, Output } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { Shell, PageComponent, FormComponent, Page, PageService, FormInput, 
         Redirection, AppConfig, Validated, MonthValidator, DayOfMonthValidator, 
         YearValidator, FieldError } from '@tytapp/common';
import { User, UserProfile } from '@tytapp/models';
import { UserService } from '../user.service';
import { UsersApi, ApiUserProfile } from '@tytapp/api';
import { ImageSaveOperation } from '../image-uploader/image-uploader.component';
import * as countryList from 'country-list';
import { Subject } from 'rxjs';

export interface Country {
  name : string;
  code : string;
}

@Page('profile')
@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss']
})
export class ProfileComponent extends PageComponent implements FormComponent {

  @FormInput()
  displayName: string = "";

  @FormInput()
  firstName: string = "";

  @FormInput()
  lastName: string = "";

  @FormInput()
  city: string = "";

  @FormInput()
  state: string = "";

  @FormInput()
  zip : string = "";

  @FormInput()
  country : string = "";

  @FormInput()
  username : string = "";

  @FormInput()
  facebookUrl: string = "";

  @FormInput()
  twitterHandle: string = "";

  @FormInput()
  instagramHandle: string = "";

  @FormInput()
  linkedinHandle: string = "";

  @FormInput()
  disablePublic: boolean = false;

  @Validated({ class: MonthValidator, field: 'month' })
  @FormInput()
  month: string = "";

  @Validated({ class: DayOfMonthValidator, field: 'day' })
  @FormInput()
  day: string = "";

  @Validated({ class: YearValidator, field: 'year' })
  @FormInput()
  year: string = "";

  @FormInput()
  gender: string = "";

  @FormInput()
  aboutMe: string = "";

  @FormInput()
  metadata: any = "";

  @FormInput()
  favoriteInspirationalFigure: string = "";

  @FormInput()
  tagline: string = "";

  @FormInput()
  favoriteQuote: string = "";

  @FormInput()
  favoriteTYTMemory: string = "";

  @FormInput()
  politicalViews: string = "";

  @FormInput()
  religionOrPhilosophy: string = "";

  updateSuccessMessage: string = '';
  edit: boolean = false;

  @Input()
  user: User;

  @Output()
  saved = new Subject<void>();
  
  countries : Country[];

  pronouns = [
    { label: 'She', selected: false }, 
    { label: 'Her', selected: false }, 
    { label: 'He', selected: false }, 
    { label: 'Him', selected: false }, 
    { label: 'They', selected: false }, 
    { label: 'Them', selected: false }, 
    { label: 'My name only', selected: false }
  ];

  constructor(
    pageService: PageService,
    private userService: UserService,
    private userApi : UsersApi
  ) {
    super(pageService);
  }

  init() {
    let map = countryList.getCodeList();
    let countries : Country[] = [];

    for (let code of Object.keys(map)) {
      countries.push({ code: code.toUpperCase(), name: map[code] });
    }

    countries = countries.sort((a, b) => {
      if (a.code === 'US')
        return -1;
        
      return a.name.localeCompare(b.name) 
    });

    this.countries = countries;

    this.subscribe(this.userService.userChanged, user => {
      this.user = user;
      this.initializeFields();
    });
  }

  async saveAvatar(operation : ImageSaveOperation) {
    let profile : ApiUserProfile;

    try {
      profile = await this.userApi.updateAvatar({ avatar_url: operation.imageUrl }).toPromise();
    } catch (e) {
      if (e.json)
        e = e.json();
      
      console.error(e);
      alert(`Failed to upload image. Please try again.`);
      operation.failed();
      return;
    }
    
    this.user.profile = profile;
    this.userService.locallyUpdateUser(this.user);

    // Force spot.im to do a full relogin on next comment box
    window['spotimInitialSync'] = false; 

    operation.completed();
  }

  async saveCover(operation : ImageSaveOperation) {
    let profile : ApiUserProfile;

    try {
      profile = await this.userApi.updateCoverPhoto({ cover_photo_url: operation.imageUrl }).toPromise();
    } catch (e) {
      if (e.json)
        e = e.json();
      
      console.error(e);
      alert(`Failed to upload image. Please try again.`);
      operation.failed();
      return;
    }
    
    this.user.profile = profile;
    this.userService.locallyUpdateUser(this.user);

    // Force spot.im to do a full relogin on next comment box
    window['spotimInitialSync'] = false; 

    operation.completed();
  }

  avatarStep : string = 'default';
  coverStep : string = 'default';

  get localBirthday() {
      if (!this.user || !this.user.profile || !this.user.profile.birthday)
          return null;
      
      let birthdate = new Date(this.user.profile.birthday);
      return new Date(birthdate.getTime() + birthdate.getTimezoneOffset() * 60 * 1000);
  }

  zeroPad(number : string, count : number) {
    while (number.length < count)
      number = '0' + number;

    return number;
  }

  initializeFields() {
    
    if (this.user && this.user.profile) {
      let profile : UserProfile = <UserProfile> this.user.profile;

      this.displayName = profile.display_name;
      this.firstName = profile.first_name;
      this.lastName = profile.last_name;
      this.city = profile.city;
      this.state = profile.state;
      this.zip = profile.zip;
      this.country = profile.country;
      this.username = profile.username;
      this.facebookUrl = profile.facebook_profile;
      this.twitterHandle = profile.twitter_profile;
      this.instagramHandle = profile.instagram_profile;
      this.linkedinHandle = profile.linkedin_url;
      this.disablePublic = !profile.is_public;
      this.favoriteInspirationalFigure = profile.favorite_inspirational_figure;
      this.tagline = profile.tagline;
      this.favoriteQuote = profile.favorite_quote;
      this.favoriteTYTMemory = profile.favorite_tyt_memory;
      this.politicalViews = profile.political_views;
      this.religionOrPhilosophy = profile.religion_or_philosophy;

      let birthday = this.localBirthday;

      if (birthday) {
        this.month = this.zeroPad("" + (birthday.getMonth() + 1), 2);
        this.day = this.zeroPad("" + (birthday.getDate()), 2);

        if (birthday.getFullYear() > 4)
          this.year = this.zeroPad("" + (birthday.getFullYear()), 2);
        else 
          this.year = null;
      }
      this.gender = profile.gender;
      this.aboutMe = profile.bio;
      this.metadata = profile.metadata || {};

      if(profile.pronouns) {
        const profilePronouns = profile.pronouns.split(',');
        for(let pronoun of this.pronouns) {
          pronoun.selected = profilePronouns.indexOf(pronoun.label) != -1;
        }
      }
    } else {
      this.displayName = '';
      this.firstName = '';
      this.lastName = '';
      this.city = '';
      this.state = '';
      this.facebookUrl = '';
      this.username = '';
      this.twitterHandle = '';
      this.instagramHandle = '';
      this.linkedinHandle = '';
      this.month = '';
      this.day = '';
      this.year = '';
      this.gender = '';
      this.aboutMe = '';
      this.disablePublic = false;
      this.favoriteInspirationalFigure = '';
      this.tagline = '';
      this.favoriteQuote = '';
      this.favoriteTYTMemory = '';
      this.politicalViews = '';
      this.religionOrPhilosophy = '';

      for(let pronoun of this.pronouns) {
        pronoun.selected = false;
      }
    }
  }

  async submit() {

    if (!this.validate())
      return;

    const selectedPronounList = [];

    console.log('pronouns');
    console.log(this.pronouns);

    for(let pronoun of this.pronouns) {
      if(pronoun.selected) {
        selectedPronounList.push(pronoun.label);
      }
    }

    const editFieldData = {
      display_name: this.displayName,
      first_name: this.firstName,
      last_name: this.lastName,
      city: this.city,
      state: this.state? this.state.toUpperCase(): null,
      facebook_profile: this.facebookUrl,
      twitter_profile: this.twitterHandle,
      instagram_profile: this.instagramHandle,
      linkedin_url: this.linkedinHandle,
      username: this.username,
      gender: this.gender,
      birthday: null,
      bio: this.aboutMe,
      metadata: this.metadata,
      country: this.country,
      zip: this.zip,
      updateAvatar: false,
      is_public: !this.disablePublic,
      pronouns: selectedPronounList.length > 0 ? selectedPronounList.join() : null,
      tagline: this.tagline,
      favorite_inspirational_figure: this.favoriteInspirationalFigure,
      favorite_quote: this.favoriteQuote,
      favorite_tyt_memory: this.favoriteTYTMemory,
      political_views: this.politicalViews,
      religion_or_philosophy: this.religionOrPhilosophy
    }

    if (this.month || this.day) {

      let fieldErrors : FieldError[] = [];

      if (!this.month)
        fieldErrors.push({field: 'month', message: '(1 - 12)'});
      if (!this.day)
        fieldErrors.push({field: 'day', message: '(1 - 31)'});

      if (!this.month || !this.day) {
        this.showError('To save your birthday we need both month and day. Year is optional.', fieldErrors);
        return;
      }
      
      let year = this.year || '0004'
      editFieldData.birthday = `${year}-${this.month}-${this.day}`
    }

    if (editFieldData.username && editFieldData.username.length > 50) {
      this.showError(`Your username is too long. Please choose a username that is no longer than 50 characters (your choice was ${editFieldData.username.length})`);
      return
    }
    
    try {
      await this.userApi.updateProfile(editFieldData).toPromise();
    } catch (e) {
      if (e.json)
        e = e.json();
      this.showError(`Could not update your profile information: ${e.message || e}.`);
      return
    }

    this.userService.userChanged.next(<User> await this.userApi.getCurrentUser().toPromise());

    this.edit = false;
    this.updateSuccessMessage = "Your profile information has been updated";
    this.saved.next();
    
    setTimeout(() => {
      this.updateSuccessMessage = '';
    }, 5000)
  }

  private capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  cancelEdit() {
    this.initializeFields();
    this.edit = false
  }

}
